/** @format */

// Copied from formspree/docs src/components/ContactSupport.tsx. Keep behavior in step with it.
//
// Three-step contact-support drawer. The flow is: (1) describe the issue,
// (2) we call /api/ask to suggest a docs article and let the user
// self-resolve, (3) if that didn't help, they POST a ticket to `ticketUrl`
// with their description, account details and the AI suggestion attached as
// context.
import {
  DocumentTextIcon,
  ExternalLinkIcon,
  MapIcon,
  NewspaperIcon,
  ViewGridIcon,
} from '@heroicons/react/outline'
import React, {useEffect, useMemo, useRef, useState} from 'react'

import {AnalyticsEvent, sendAnalytics} from '@src/analytics'

interface Source {
  slug: string
  title: string
  section: string
  locale: string
}

interface AskAnswer {
  text: string
  sources: Source[]
}

type Step = 'describe' | 'suggest' | 'ticket' | 'done'

// Thrown by fetchSuggestion when /api/ask answers with an error.
class AskFailed extends Error {
  constructor(readonly status: number) {
    super('ask failed: ' + status)
  }
}

// Default for how many sources the drawer lists, and attaches to a ticket.
// The docs /api/ask returns at most 6.
const MAX_SOURCES = 6

interface Props {
  askUrl: string
  ticketUrl: string
  responseDays: number
  metadata: Record<string, string | number>
  // categorized    sources grouped by type, one icon and type label per group
  // uncategorized  one plain list of links, in the order /api/ask ranked them
  sourceLayout?: 'categorized' | 'uncategorized'
  maxSources?: number
  // Called with true when the drawer opens, and false as soon as it starts
  // to close, so a page's own button can reappear in time to take focus back.
  onOpenChange?: (open: boolean) => void
}

// What kind of page a source is, read from its formspree.io path. Help docs
// have a bare slug, and so does anything unrecognised.
function sourceKind(s: Source) {
  const path = /^https?:\/\//i.test(s.slug) ? new URL(s.slug).pathname : ''
  if (path.startsWith('/guides/')) return {label: 'Guides', Icon: MapIcon}
  if (path.startsWith('/blog/'))
    return {label: 'Blog posts', Icon: NewspaperIcon}
  if (path.startsWith('/library/'))
    return {label: 'Form library', Icon: ViewGridIcon}
  return {label: 'Help docs', Icon: DocumentTextIcon}
}

// Sources grouped by kind, each group placed where its first source ranked.
function groupSources(sources: Source[]) {
  const groups: (ReturnType<typeof sourceKind> & {sources: Source[]})[] = []
  for (const s of sources) {
    const kind = sourceKind(s)
    const group = groups.find(g => g.label === kind.label)
    if (group) group.sources.push(s)
    else groups.push({...kind, sources: [s]})
  }
  return groups
}

// frontsite-hugo indexes guides and blog posts with their full URL as the
// slug; help-docs articles use a bare slug under the help site's /articles.
function articleHref(s: Source, helpOrigin: string): string {
  if (/^https?:\/\//i.test(s.slug)) return s.slug
  return helpOrigin + '/articles/' + s.slug
}

// Pull the assistant's streamed plain-text body and the X-Sources header
// (base64 JSON) from /api/ask. Mirrors the AskAIDrawer wire format.
// `onText` gets the answer so far after each chunk, so the drawer can show it
// while the rest streams in: the first chunk takes a second or two, the whole
// answer around ten.
async function fetchSuggestion(
  question: string,
  askUrl: string,
  onText: (answer: AskAnswer) => void,
): Promise<AskAnswer> {
  const res = await fetch(askUrl, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    // `mode: 'support-intervention'` switches the server-side system
    // prompt to one that won't tell the user to "contact support" —
    // they're already in the support flow, with a ticket button right
    // below the suggestion.
    body: JSON.stringify({
      q: question,
      locale: 'en-us',
      history: [],
      mode: 'support-intervention',
    }),
  })
  if (!res.ok || !res.body) throw new AskFailed(res.status)

  let sources: Source[] = []
  const sourcesHeader = res.headers.get('X-Sources')
  if (sourcesHeader) {
    try {
      const binary = atob(sourcesHeader)
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
      sources = JSON.parse(new TextDecoder().decode(bytes))
    } catch {
      sources = []
    }
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let text = ''
  while (true) {
    const {done, value} = await reader.read()
    if (done) break
    text += decoder.decode(value, {stream: true})
    onText({text, sources})
  }
  return {text, sources}
}

// Markdown renderer for the assistant reply. Mirrors the renderer in
// AskAIDrawer (paragraphs, headings, lists, blockquotes, bold/italic,
// inline + fenced code, links) and resolves the model's bracketed
// article citations against the source list so they become real links.
function escapeHtmlMd(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

type SourceMap = Record<string, {title: string; href: string}>

function buildSourceMap(sources: Source[], helpOrigin: string): SourceMap {
  const map: SourceMap = {}
  for (const s of sources) {
    const title = (s.title || '').trim()
    if (!title) continue
    map[title.toLowerCase()] = {title, href: articleHref(s, helpOrigin)}
  }
  return map
}

// Replace `[Article Title]` with a link if the title matches a source.
// Multiple titles inside one bracket pair (the model sometimes does
// this) are split on commas and resolved individually.
function applyCitations(text: string, sourceMap: SourceMap): string {
  return text.replace(/\[([^\]\n]+)\](?!\()/g, (full, label: string) => {
    const parts = label.split(/\s*,\s*/)
    const out: string[] = []
    let any = false
    for (const raw of parts) {
      const hit = sourceMap[raw.trim().toLowerCase()]
      if (hit) {
        any = true
        out.push(
          `<a href="${hit.href.replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer">${escapeHtmlMd(hit.title)}</a>`,
        )
      } else {
        out.push(raw)
      }
    }
    return any ? out.join(', ') : full
  })
}

// The output goes into dangerouslySetInnerHTML on a logged-in page, so only
// http(s) and root-relative help-site URLs become links; anything else
// (javascript:, data:, relative paths) renders as its label alone.
function renderInline(text: string, helpOrigin: string): string {
  let t = text.replace(
    /\[([^\]]+)\]\(([^)\s]+)\)/g,
    (_, label: string, url: string) => {
      let href: string
      if (/^https?:\/\//i.test(url)) href = url
      else if (url.startsWith('/')) href = helpOrigin + url
      else return label
      return `<a href="${href.replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer">${label}</a>`
    },
  )
  t = t.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
  t = t.replace(/(^|[\s(>])\*([^*\n]+)\*(?=[\s).,!?:;]|$)/g, '$1<em>$2</em>')
  return t
}

function renderMarkdown(
  md: string,
  sources: Source[],
  helpOrigin: string,
): string {
  if (!md) return ''
  const sourceMap = buildSourceMap(sources, helpOrigin)

  // Pull out fenced code blocks first so their contents bypass the
  // inline rules. Use SOH (\u0001) as the placeholder delimiter — NUL
  // gets stripped by the DOM during innerHTML, SOH survives.
  const codeBlocks: {lang: string; code: string}[] = []
  const BLOCK = (i: number) => `\n\n\u0001BLOCK${i}\u0001\n\n`
  let body = md.replace(
    /```([^\n`]*)\n([\s\S]*?)```/g,
    (_, lang: string, code: string) => {
      codeBlocks.push({lang: lang.trim(), code})
      return BLOCK(codeBlocks.length - 1)
    },
  )

  // Inline code.
  const inlineCodes: string[] = []
  body = body.replace(/`([^`\n]+)`/g, (_, code: string) => {
    inlineCodes.push(code)
    return `\u0001CODE${inlineCodes.length - 1}\u0001`
  })

  body = escapeHtmlMd(body)

  const html = body
    .split(/\n{2,}/)
    .map(block => {
      const trimmed = block.replace(/^\n+|\n+$/g, '')
      if (!trimmed) return ''

      const blockMatch = trimmed.match(/^\u0001BLOCK(\d+)\u0001$/)
      if (blockMatch) {
        const entry = codeBlocks[parseInt(blockMatch[1], 10)]
        const escapedCode = escapeHtmlMd(entry.code).replace(/\n+$/, '')
        const langClass = entry.lang
          ? ` class="language-${entry.lang.replace(/[^a-zA-Z0-9_+-]/g, '')}"`
          : ''
        return `<pre><code${langClass}>${escapedCode}</code></pre>`
      }

      const head = trimmed.match(/^(#{1,6})\s+([\s\S]+)$/)
      if (head) {
        const level = Math.min(head[1].length, 4)
        return `<h${level}>${renderInline(applyCitations(head[2], sourceMap), helpOrigin)}</h${level}>`
      }

      if (/^&gt;\s/.test(trimmed) || /^>\s/.test(trimmed)) {
        const content = trimmed.replace(/^(?:&gt;|>)\s?/gm, '')
        return `<blockquote>${renderInline(applyCitations(content, sourceMap), helpOrigin).replace(/\n/g, '<br>')}</blockquote>`
      }

      const lines = trimmed.split('\n')
      const isUl =
        lines.every(l => /^\s*[-*]\s+/.test(l) || /^\s+/.test(l)) &&
        /^\s*[-*]\s+/.test(lines[0])
      const isOl =
        lines.every(l => /^\s*\d+\.\s+/.test(l) || /^\s+/.test(l)) &&
        /^\s*\d+\.\s+/.test(lines[0])
      if (isUl || isOl) {
        const items: string[] = []
        let current = ''
        for (const line of lines) {
          const m = line.match(/^\s*(?:[-*]|\d+\.)\s+(.*)$/)
          if (m) {
            if (current) items.push(current)
            current = m[1]
          } else {
            current += '\n' + line.trim()
          }
        }
        if (current) items.push(current)
        const tag = isOl ? 'ol' : 'ul'
        return `<${tag}>${items
          .map(
            i =>
              `<li>${renderInline(applyCitations(i, sourceMap), helpOrigin).replace(/\n/g, '<br>')}</li>`,
          )
          .join('')}</${tag}>`
      }

      return `<p>${renderInline(applyCitations(trimmed, sourceMap), helpOrigin).replace(/\n/g, '<br>')}</p>`
    })
    .join('')

  let out = html.replace(
    /\u0001CODE(\d+)\u0001/g,
    (_, i: string) =>
      `<code>${escapeHtmlMd(inlineCodes[parseInt(i, 10)])}</code>`,
  )
  out = out.replace(/\u0001BLOCK(\d+)\u0001/g, (_, i: string) => {
    const entry = codeBlocks[parseInt(i, 10)]
    if (!entry) return ''
    const escapedCode = escapeHtmlMd(entry.code).replace(/\n+$/, '')
    const langClass = entry.lang
      ? ` class="language-${entry.lang.replace(/[^a-zA-Z0-9_+-]/g, '')}"`
      : ''
    return `<pre><code${langClass}>${escapedCode}</code></pre>`
  })
  return out
}

export default function ContactSupport({
  askUrl,
  ticketUrl,
  responseDays,
  metadata,
  sourceLayout = 'categorized',
  maxSources = MAX_SOURCES,
  onOpenChange,
}: Props) {
  const helpOrigin = new URL(askUrl).origin
  const [open, setOpen] = useState(false)
  // While `closing` is true the drawer stays mounted but renders with
  // the reverse animation; once that finishes we flip `open` off and
  // unmount. This mirrors the open transition (slide-in + fade-in)
  // instead of letting the panel disappear instantly on dismiss.
  const [closing, setClosing] = useState(false)
  const [step, setStep] = useState<Step>('describe')
  const [description, setDescription] = useState('')
  const [suggestion, setSuggestion] = useState<AskAnswer | null>(null)
  // The question `suggestion` answers. `description` stays editable on the
  // ticket step, so it can drift from what the AI was asked.
  const [askedQuestion, setAskedQuestion] = useState('')
  const [askBusy, setAskBusy] = useState(false)
  const [askError, setAskError] = useState(false)
  const [emailInput, setEmailInput] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(false)
  // Tracks how we got to the `done` step, so the done view can say
  // "Ticket sent" or "Glad that helped".
  const [doneReason, setDoneReason] = useState<'resolved' | 'submitted' | null>(
    null,
  )
  const triggerRef = useRef<HTMLElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const closeTimerRef = useRef<number | undefined>(undefined)
  // Read by the close timer, which Esc starts from a closure made when the
  // drawer opened, so `step` there is stale.
  const stepRef = useRef(step)
  stepRef.current = step

  // Listen for the global open trigger fired by the page's support button.
  // Using a custom event keeps this component independent of whatever
  // renders the button — no shared state, just a one-way signal.
  useEffect(() => {
    function onOpen(e: Event) {
      // Focus goes back here on close: the trigger the opener passed, or else
      // whatever had focus, like the link that called showForm.
      triggerRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
      if (e instanceof CustomEvent) {
        const detail: unknown = e.detail
        if (
          typeof detail === 'object' &&
          detail !== null &&
          'trigger' in detail &&
          detail.trigger instanceof HTMLElement
        ) {
          triggerRef.current = detail.trigger
        }
      }
      // Reopened mid-close: keep the pending close from shutting it again.
      window.clearTimeout(closeTimerRef.current)
      setClosing(false)
      setOpen(true)
    }
    window.addEventListener('open-contact-support', onOpen)

    // Deep link: any URL ending in #contact opens the drawer on load.
    // Checked here (after mount) rather than by the page so we never
    // dispatch before this listener exists. The hash is stripped after
    // consuming it so closing + refreshing doesn't reopen the drawer.
    function consumeHash() {
      if (window.location.hash !== '#contact') return
      triggerRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
      history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search,
      )
      window.clearTimeout(closeTimerRef.current)
      setClosing(false)
      setOpen(true)
    }
    consumeHash()
    window.addEventListener('hashchange', consumeHash)

    return () => {
      window.removeEventListener('open-contact-support', onOpen)
      window.removeEventListener('hashchange', consumeHash)
    }
  }, [])

  // Begin the close animation. After the panel slide-out finishes we
  // unmount. Duration matches the slide-in keyframe (220ms); the
  // shorter backdrop fade (180ms) finishes within the same window so
  // a single timer is enough.
  //
  // A finished request (ticket sent or self-resolved) is cleared on close,
  // so the next open starts a new one. Anything earlier is kept, so closing
  // by accident doesn't lose a half-written question.
  function requestClose() {
    if (!open || closing) return
    setClosing(true)
    closeTimerRef.current = window.setTimeout(() => {
      setClosing(false)
      setOpen(false)
      if (stepRef.current === 'done') reset()
    }, 220)
  }

  useEffect(
    () => onOpenChange?.(open && !closing),
    [open, closing, onOpenChange],
  )

  // Lock body scroll while open + restore focus to the trigger on close.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      setTimeout(() => textareaRef.current?.focus(), 60)
      return () => {
        document.body.style.overflow = prev
      }
    }
    if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
      triggerRef.current.focus()
    }
    return undefined
  }, [open])

  // Esc closes the drawer.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') requestClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function reset() {
    setStep('describe')
    setDescription('')
    setSuggestion(null)
    setAskedQuestion('')
    setAskError(false)
    setEmailInput('')
    setError(false)
    setDoneReason(null)
  }

  async function onDescribeSubmit(e: React.FormEvent) {
    e.preventDefault()
    const q = description.trim()
    if (!q || askBusy) return
    setAskBusy(true)
    setAskError(false)
    try {
      const answer = await fetchSuggestion(q, askUrl, partial => {
        setSuggestion(partial)
        setStep('suggest')
      })
      setSuggestion(answer)
      setAskedQuestion(q)
      setStep('suggest')
      sendAnalytics(AnalyticsEvent.support_suggestion_shown, {
        sources: dedupeSources(answer.sources).length,
      })
    } catch (err) {
      sendAnalytics(AnalyticsEvent.support_ask_failed, {
        status: err instanceof AskFailed ? err.status : 0,
      })
      // The /api/ask endpoint can be cold, down or rate-limited. Skip
      // straight to the ticket form so the user isn't stuck behind a
      // broken assistant.
      setAskError(true)
      setSuggestion(null)
      setStep('ticket')
    } finally {
      setAskBusy(false)
    }
  }

  // Pre-built context for the support ticket: the docs answer and articles
  // we suggested, so the human responder sees what led the user to escalate.
  const ticketContext = useMemo(() => {
    if (!suggestion) return ''
    const lines: string[] = []
    lines.push('— AI suggestion shown to user —')
    lines.push(`Question asked: ${askedQuestion}`)
    if (suggestion.text.trim()) lines.push('', suggestion.text.trim())
    if (suggestion.sources.length) {
      lines.push('')
      lines.push('Suggested articles:')
      for (const s of dedupeSources(suggestion.sources).slice(0, maxSources)) {
        lines.push(`- ${s.title} — ${articleHref(s, helpOrigin)}`)
      }
    }
    return lines.join('\n')
  }, [suggestion, askedQuestion, helpOrigin, maxSources])

  async function onTicketSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    const message = description.trim()
    const {email, ...details} = metadata
    const plan = String(metadata.Plan ?? '')
    const urgent = plan.includes('platinum') || plan.includes('business')
    setSubmitting(true)
    setError(false)
    try {
      const res = await fetch(ticketUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email: email || emailInput,
          _message: message,
          _subject:
            'Feedback: ' +
            message.slice(0, 30) +
            (message.length > 30 ? '...' : ''),
          _tags: 'feedback_button',
          ...(urgent ? {_urgent: true} : {}),
          ...details,
          ...(ticketContext ? {ai_context: ticketContext} : {}),
        }),
      })
      if (!res.ok) throw new Error('ticket failed: ' + res.status)
      sendAnalytics(AnalyticsEvent.support_ticket_submitted, {
        after_suggestion: suggestion !== null,
      })
      setDoneReason('submitted')
      setStep('done')
    } catch {
      setError(true)
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  const sources = suggestion
    ? dedupeSources(suggestion.sources).slice(0, maxSources)
    : []

  return (
    <div
      className={'cs-root' + (closing ? ' is-closing' : '')}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cs-title"
    >
      <div className="cs-backdrop" onClick={requestClose} />
      <aside className="cs-panel" ref={panelRef} tabIndex={-1}>
        <header className="cs-header">
          <div className="cs-header-title">
            <svg
              className="cs-header-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <h2 id="cs-title">Contact support</h2>
          </div>
          <button
            type="button"
            className="cs-icon-btn"
            onClick={requestClose}
            aria-label="Close"
            title="Close"
          >
            <svg
              className="cs-x"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </header>

        <div className="cs-body">
          {step === 'describe' && (
            <form onSubmit={onDescribeSubmit} className="cs-form">
              <label htmlFor="cs-description" className="cs-label">
                Description
              </label>
              <textarea
                id="cs-description"
                ref={textareaRef}
                className="cs-textarea"
                rows={6}
                placeholder="Describe what you’re trying to do and what went wrong…"
                value={description}
                onChange={e => setDescription(e.target.value)}
                required
              />
              <p className="cs-help">
                We’ll first check the docs for an answer before opening a
                ticket.
              </p>
              <button
                type="submit"
                className="cs-btn cs-btn-primary"
                disabled={askBusy || !description.trim()}
              >
                {askBusy ? 'Searching the docs…' : 'Find an answer'}
              </button>
            </form>
          )}

          {step === 'suggest' && suggestion && (
            <div className="cs-suggest">
              <p className="cs-suggest-lead">
                {suggestion.text.trim()
                  ? 'Here’s what we found in the docs:'
                  : 'We couldn’t find a confident match in the docs.'}
              </p>
              {suggestion.text.trim() && (
                <div
                  className="cs-suggest-body"
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(
                      suggestion.text,
                      suggestion.sources,
                      helpOrigin,
                    ),
                  }}
                />
              )}
              {sources.length > 0 &&
                (sourceLayout === 'categorized' ? (
                  <ul className="cs-sources">
                    {groupSources(sources).map(
                      ({label, Icon, sources: group}) => (
                        <li key={label} className="cs-source-group">
                          <span className="cs-source-tile">
                            <Icon
                              className="cs-source-icon"
                              aria-hidden="true"
                            />
                          </span>
                          <div className="cs-source-group-body">
                            <p className="cs-source-kind">{label}</p>
                            <ul aria-label={label} className="cs-source-links">
                              {group.map(s => (
                                <li key={s.locale + ':' + s.slug}>
                                  <a
                                    href={articleHref(s, helpOrigin)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="cs-source-link"
                                  >
                                    <span className="cs-source-title">
                                      {s.title}
                                    </span>
                                    <ExternalLinkIcon
                                      className="cs-source-external"
                                      aria-hidden="true"
                                    />
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </li>
                      ),
                    )}
                  </ul>
                ) : (
                  <ul className="cs-source-list">
                    {sources.map(s => (
                      <li
                        key={s.locale + ':' + s.slug}
                        className="cs-source-row"
                      >
                        <DocumentTextIcon
                          className="cs-source-row-icon"
                          aria-hidden="true"
                        />
                        <a
                          href={articleHref(s, helpOrigin)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cs-source-row-link"
                        >
                          {s.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                ))}
              {/* Not until the answer has finished streaming, so a ticket
                  carries all of it. */}
              {!askBusy && (
                <div className="cs-actions">
                  <button
                    type="button"
                    className="cs-btn cs-btn-primary"
                    onClick={() => {
                      sendAnalytics(AnalyticsEvent.support_resolved)
                      setDoneReason('resolved')
                      setStep('done')
                    }}
                  >
                    This solves my issue
                  </button>
                  <button
                    type="button"
                    className="cs-btn cs-btn-secondary"
                    onClick={() => setStep('ticket')}
                  >
                    I still need help — submit a ticket
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 'ticket' && (
            <form onSubmit={onTicketSubmit} className="cs-form">
              <p className="cs-help">
                {askError
                  ? 'Couldn’t reach the assistant. You can still submit a ticket.'
                  : 'A human will get back to you. We’ll include your original question, your description and what we suggested.'}
              </p>

              {!metadata.email && (
                <>
                  <label htmlFor="cs-email" className="cs-label">
                    Your email
                  </label>
                  <input
                    id="cs-email"
                    name="email"
                    type="email"
                    className="cs-input"
                    placeholder="you@example.com"
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    required
                  />
                </>
              )}

              <label htmlFor="cs-ticket-description" className="cs-label">
                Description
              </label>
              <textarea
                id="cs-ticket-description"
                name="message"
                className="cs-textarea"
                rows={6}
                value={description}
                onChange={e => setDescription(e.target.value)}
                required
              />

              <div className="cs-actions">
                <button
                  type="button"
                  className="cs-btn cs-btn-secondary"
                  onClick={() => setStep(suggestion ? 'suggest' : 'describe')}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="cs-btn cs-btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Sending…' : 'Submit ticket'}
                </button>
              </div>
              {error && (
                <p className="cs-error cs-error-form">
                  Submission failed. Please try again.
                </p>
              )}
            </form>
          )}

          {step === 'done' && (
            <div className="cs-done">
              <svg
                className="cs-done-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="cs-done-title">
                {doneReason === 'submitted'
                  ? 'Ticket sent.'
                  : 'Glad that helped.'}
              </h3>
              {doneReason === 'submitted' && (
                <p className="cs-done-body">
                  We’ll get back to you within {responseDays} business days.
                </p>
              )}
              <div className="cs-actions cs-actions-center">
                <button
                  type="button"
                  className="cs-btn cs-btn-secondary"
                  onClick={() => {
                    reset()
                  }}
                >
                  Ask about something else
                </button>
                <button
                  type="button"
                  className="cs-btn cs-btn-primary"
                  onClick={requestClose}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        <footer className="cs-footer">
          <a
            href="https://formspree.io"
            target="_blank"
            rel="noopener noreferrer"
            className="cs-built"
          >
            Built using Formspree
          </a>
        </footer>
      </aside>
    </div>
  )
}

// Retrieval may surface several chunks per article. Collapse them so the
// suggestion list shows distinct articles instead of duplicate rows.
function dedupeSources(sources: Source[]): Source[] {
  const seen: Record<string, boolean> = {}
  const out: Source[] = []
  for (const s of sources) {
    const key = (s.locale || 'en-us') + ':' + s.slug
    if (seen[key]) continue
    seen[key] = true
    out.push(s)
  }
  return out
}
